import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { REHYDRATE } from 'redux-persist';
import type { RootState } from 'common/store/store';

export interface SpotlightState {
  enabled: boolean;
}

const initialState: SpotlightState = {
  // Default ON - Dan's product decision (2026-07-31 plan), chosen over
  // opt-in for discoverability. Also what a user upgrading from a build
  // that predates this preference lands on, since their persisted snapshot
  // has no `spotlight` key at all (see the REHYDRATE handler below).
  enabled: true,
};

// Mirrors `enabled` outside React/Redux. `spotlight.ts`'s native-bridge
// helpers (syncSpotlightIndex/removeFromSpotlight) need to read this
// preference themselves so no call site can forget to check it, but they
// must not import the store singleton to do so: authSlice.ts (which the
// store wires in as the auth reducer) calls into spotlight.ts too, and
// spotlight.ts importing `store` from common/store/store would complete a
// store.ts -> authSlice.ts -> spotlight.ts -> store.ts cycle. A plain
// module-level value, kept in sync only by this slice's own reducer and its
// REHYDRATE handler, sidesteps that entirely.
let cachedEnabled = initialState.enabled;

const spotlightSlice = createSlice({
  name: 'spotlight',
  initialState,
  reducers: {
    setSpotlightIndexingEnabled: (state, { payload }: PayloadAction<boolean>) => {
      state.enabled = payload;
      cachedEnabled = payload;
    },
  },
  extraReducers: (builder) => {
    // Adding this matcher exempts this slice from redux-persist's default
    // autoMergeLevel1 top-level replacement (see lockSlice.ts's own comment
    // on the same mechanism) - so the persisted value has to be copied
    // across explicitly here, same as lockSlice does for its own fields.
    builder.addMatcher(
      (action) => action.type === REHYDRATE,
      (state, action: { payload?: { spotlight?: SpotlightState } }) => {
        const persisted = action.payload?.spotlight;
        if (persisted && typeof persisted.enabled === 'boolean') {
          state.enabled = persisted.enabled;
        }
        // Absent persisted value (pre-existing install upgrading to a build
        // that has this slice for the first time) keeps initialState's
        // default of true, which is the intended behaviour.
        cachedEnabled = state.enabled;
      },
    );
  },
});

export const { setSpotlightIndexingEnabled } = spotlightSlice.actions;

export default spotlightSlice.reducer;

export const selectSpotlightIndexingEnabled = (state: RootState) => state.spotlight.enabled;

// For use outside React components/connected code - see the comment on
// `cachedEnabled` above for why this exists instead of a store import.
export const isSpotlightIndexingEnabled = (): boolean => cachedEnabled;
